import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router'
import { useSessionUser } from '@/hooks/use-session'
import { useEffect } from 'react'
import { UserMenu } from './-components/user-menu'
import { AppTopBar } from './-components/app-topbar'
import { AppBottomNav } from './-components/app-bottom-nav'
import { RootChromeProvider, TransitionOutlet } from '@/components/custom/route-transition'
import { CompanyWelcomeAutoOpen } from './-components/companies/welcome-auto-open'
import { AssistantMount } from './-components/assistant/assistant-mount'
import { getChromeVariant } from './-components/app-layout-skeleton'
import { AppShellPending, MAIN_CLASS, SHELL_CLASS } from './-components/shell-pending'
import { Guarded } from './-components/access-guard'
import { useIsEmbedded } from './-components/use-is-embedded'
import { useTopLevelSections } from './-components/use-top-level-sections'
import { FRESH_CONVERSATION } from '@/hooks/use-root-search'
import { applySlowMoFromUrl } from './-components/shared/squash-clock'

/**
 * The SIGNED-IN shell: the sections, onboarding's test steps, checkout and the tools, everything that needs a session
 * or draws the app's chrome. The pages an anonymous visitor uses live under `_public`, so none of them downloads this
 * chrome — docs/notes/app/app-shell.md.
 */
export const Route = createFileRoute('/_app')({
  // Everything under this shell is behind a session, so it is noindex unless a route's own `pageSeo` says otherwise.
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: AppLayout,
  // Its component is its own chunk. While an entry awaits it, this paints the chrome's skeleton around the route's
  // own; a mounted shell never pends again, and it never suspends: its one session read sits below the outlet's boundary.
  pendingComponent: AppShellPending,
})

function AppLayout() {
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  // `?slowmo=4|8|16` stretches the whole list squash so it can be stepped frame by frame; a no-op without it.
  useEffect(applySlowMoFromUrl, [])
  // A preview renders inside the real tree, so it draws neither chrome nor guard — docs/notes/app/app-shell.md.
  const { previewFrom } = Route.useRouteContext()
  if (previewFrom !== undefined) return <Outlet />

  const variant = getChromeVariant(routeId)
  if (variant === 'simple')
    return (
      <Guarded shell="/_app">
        <SimpleShell />
      </Guarded>
    )
  if (variant !== 'full')
    return (
      <Guarded shell="/_app">
        <Outlet />
      </Guarded>
    )
  return <FullChrome />
}

function FullChrome() {
  const embedded = useIsEmbedded()
  if (embedded) {
    return (
      <div className={SHELL_CLASS} data-app-layout="bare">
        <main className={MAIN_CLASS}>
          <div className="max-w-9xl mx-auto h-full w-full">
            <Guarded shell="/_app">
              <Outlet />
            </Guarded>
          </div>
        </main>
      </div>
    )
  }

  // The chrome renders at once: the topbar and the bottom bar carry their own skeleton fallbacks, and the pane holds
  // the route's, so nothing here waits for the session.
  return (
    <div className={SHELL_CLASS} data-app-layout>
      <AppTopBar placement="shell" />
      {/* Edge to edge: the scroller is the window, not a card inset from it. The CONTENT keeps `max-w-9xl`
          and centres inside — the box extends, the reading width does not. */}
      <main className={MAIN_CLASS}>
        <div className="max-w-9xl mx-auto h-full w-full">
          <Guarded shell="/_app">
            {/* The phone's in-pane copy, handed to the stack's ROOT screen so a push carries it. */}
            <RootChromeProvider chrome={<AppTopBar placement="pane" />}>
              <SectionPager />
            </RootChromeProvider>
          </Guarded>
        </div>
      </main>
      {/* Mobile only: the section links live down here instead of in the topbar — the last row of a shell exactly
          one viewport tall, under the pane that owns the scroll, so it stays reachable without overlaying content. */}
      <AppBottomNav />
      <CompanyWelcomeAutoOpen />
      <AssistantMount />
    </div>
  )
}

// The app's ROOT scroll-snap surface: the viewer's top-level sections (Teams / Jobs / Offers / …) become
// swipeable full-width panes, and a section nav click glides straight there. LATERAL — the sections are
// PEERS, not levels of a hierarchy, so no depth parallax and no cast shadow. A single-section viewer gets a plain <Outlet/> (nothing to
// swipe). Only in the full (non-embedded) chrome; the embedded SDK shell keeps a plain outlet.
function SectionPager() {
  const sections = useTopLevelSections()
  if (sections.length <= 1) return <Outlet />
  // h-full (not flex-1): <main> is a block with a definite height, so the stage fills it and the sections'
  // own `h-full` cascades — the same contract the plain <Outlet/> had here.
  return (
    <TransitionOutlet
      group={sections}
      className="h-full"
      enterSearch={(prev) => ({ ...prev, ...FRESH_CONVERSATION })}
    />
  )
}

function SimpleShell() {
  const user = useSessionUser()
  const embedded = useIsEmbedded()

  return (
    <>
      {user && !embedded && (
        <div className="flex justify-end px-4 pt-3 print:hidden">
          <UserMenu />
        </div>
      )}
      <Outlet />
    </>
  )
}
